import { useMemo, useState, type FC } from "react";
import type { DashboardViewProps } from "../../types/dashboardView";
import { AnalyticChartPanel } from "./analyticShared/AnalyticChartPanel";
import { AnalyticLegend } from "./analyticShared/AnalyticLegend";
import { remainingWeightSeries } from "./analyticShared/completionSeries";
import { useScopeChildren } from "./analyticShared/scopeChildren";
import { useChartBox } from "./analyticShared/useChartBox";
import { CHART_THEME_COLORS } from "../Common/Chart/chartTheme";
import { AreaChart } from "../Common/Chart/AreaChart";
import type { ChartResult, ChartSpec } from "../../types/chart";

const TICKS = 14;

export const BurnshoreView: FC<DashboardViewProps> = ({
  elements,
  viewStartMs,
  viewEndMs,
  recurringOccurrencesById = new Map(),
}) => {
  const [currentMs] = useState(() => Date.now());
  const { parentRef, width, height } = useChartBox();
  const scope = useScopeChildren(elements);
  const ids = useMemo(
    () => new Map(elements.map((element) => [element.id, element])),
    [elements],
  );
  const raw = useMemo(
    () =>
      remainingWeightSeries(
        scope.children,
        ids,
        { startMs: viewStartMs, endMs: viewEndMs },
        TICKS,
      ),
    [scope.children, ids, viewStartMs, viewEndMs],
  );
  const order = useMemo(
    () =>
      raw
        .map((_, index) => index)
        .sort((a, b) => (raw[b][7] ?? 0) - (raw[a][7] ?? 0)),
    [raw],
  );
  const children = order.map((index) => scope.children[index]);
  const series = order.map((index) => raw[index]);
  const colors = useMemo(
    () =>
      children.map((child, i) =>
        child.ownColor && /^#[0-9a-fA-F]{6}$/.test(child.ownColor)
          ? child.ownColor
          : CHART_THEME_COLORS[i % CHART_THEME_COLORS.length],
      ),
    [children],
  );

  const caption = (
    <>
      Scope: <strong>{scope.parent?.title ?? "Board"}</strong> · showing{" "}
      <strong>{children.length}</strong> {scope.nextLevelName}s.
    </>
  );
  const legend = children.length ? (
    <AnalyticLegend
      children={children}
      colors={colors}
      nextLevelName={scope.nextLevelName}
      onDrillInto={scope.drillInto}
      terminal={scope.nextLevelName === "checklist"}
    />
  ) : null;

  const maxTotal = useMemo(() => {
    const totals = Array.from({ length: TICKS }, (_, tick) =>
      series.reduce((sum, row) => sum + (row[tick] ?? 0), 0),
    );
    return Math.max(0, ...totals);
  }, [series]);

  if (!children.length)
    return (
      <AnalyticChartPanel
        title="Burnshore"
        hint="Weighted remaining work."
        scopeCaption={caption}
        legend={legend}
      >
        <div className="analyticEmpty">This scope has no children yet.</div>
      </AnalyticChartPanel>
    );
  if (!maxTotal)
    return (
      <AnalyticChartPanel
        title="Burnshore"
        hint="Weighted remaining work."
        scopeCaption={caption}
        legend={legend}
      >
        <div className="analyticCelebrate">Burned down ✓</div>
      </AnalyticChartPanel>
    );

  const totalStart = series.reduce((sum, row) => sum + (row[0] ?? 0), 0);

  const seriesColorMap: Record<string, string> = {};
  children.forEach((child) => {
    if (child.ownColor && /^#[0-9a-fA-F]{6}$/.test(child.ownColor)) {
      seriesColorMap[child.id] = child.ownColor;
    }
  });

  const chartSpec: ChartSpec = {
    version: 1,
    id: "burnshore-chart",
    workspaceId: "views",
    title: "Burnshore",
    dataset: "work",
    type: "area",
    measure: { field: "count", agg: "sum" },
    range: { kind: "all" },
    filters: [],
    style: {
      palette: Object.keys(seriesColorMap).length > 0 ? "custom" : "theme",
      seriesColor: Object.keys(seriesColorMap).length > 0 ? seriesColorMap : undefined,
      legend: "off",
      dataLabels: false,
      gridlines: true,
    },
    createdBy: "system",
    createdAt: new Date().toISOString(),
    updatedBy: "system",
    updatedAt: new Date().toISOString(),
  };

  const chartData: ChartResult = {
    asOf: new Date().toISOString(),
    series: children.map((child, childIdx) => ({
      key: child.id,
      label: child.title,
      points: Array.from({ length: TICKS }, (_, tick) => ({
        x: `T${tick + 1}`,
        y: series[childIdx]?.[tick] ?? 0,
      })),
    })),
  };

  const now = Math.max(viewStartMs, Math.min(viewEndMs, currentMs));
  const diamonds = [...recurringOccurrencesById.values()]
    .flat()
    .filter(
      (item) =>
        item.occurrenceStartMs >= viewStartMs &&
        item.occurrenceStartMs <= viewEndMs,
    );

  return (
    <AnalyticChartPanel
      title="Burnshore"
      hint={
        <>
          Weighted burndown per <em>{scope.nextLevelName}</em>, with checklist
          occurrences.
        </>
      }
      scopeCaption={caption}
      legend={legend}
    >
      <div ref={parentRef} style={{ width: "100%" }}>
        <AreaChart
              spec={chartSpec}
              data={chartData}
              width={width}
              height={height}
              stack="absolute"
              testId="burnshore-areachart"
              onSeriesClick={
                scope.nextLevelName !== "checklist"
                  ? (childId) => scope.drillInto(childId)
                  : undefined
              }
              renderOverlay={({ xScale, yScale, innerWidth, innerHeight }) => {
                const firstTickX = xScale('T1') ?? 0;
                const lastTickX = xScale(`T${TICKS}`) ?? innerWidth;
                const idealYStart = yScale(totalStart);
                const idealYEnd = yScale(0);

                const nowRatio =
                  viewEndMs === viewStartMs
                    ? 0
                    : Math.max(0, Math.min(1, (now - viewStartMs) / (viewEndMs - viewStartMs)));
                const nowX = firstTickX + nowRatio * (lastTickX - firstTickX);

                return (
                  <g className="burnshore-overlay">
                    <line
                      className="ideal"
                      x1={firstTickX}
                      y1={idealYStart}
                      x2={lastTickX}
                      y2={idealYEnd}
                      style={{ stroke: 'var(--text-muted)' }}
                      strokeDasharray="4 3"
                    />
                    <line
                      className="now"
                      x1={nowX}
                      x2={nowX}
                      y1={0}
                      y2={innerHeight}
                      style={{ stroke: 'var(--accent)' }}
                      strokeDasharray="4 3"
                    />
                    {diamonds.map((item) => {
                      const ratio =
                        viewEndMs === viewStartMs
                          ? 0
                          : (item.occurrenceStartMs - viewStartMs) / (viewEndMs - viewStartMs);
                      const dx = firstTickX + ratio * (lastTickX - firstTickX);
                      const done = item.state === "completed";
                      const missed = item.state === "missed";
                      return (
                        <path
                          key={item.occurrenceKey}
                          className={`diamond${done ? " diamond--done" : missed ? " diamond--miss" : ""}`}
                          d={`M ${dx} ${innerHeight + 8} l 3 3 l -3 3 l -3 -3 z`}
                          fill={done ? undefined : "none"}
                          style={{
                            fill: done ? 'var(--success)' : 'none',
                            stroke: done
                              ? 'none'
                              : missed
                                ? 'var(--danger)'
                                : 'var(--text-muted)',
                          }}
                        />
                      );
                    })}
                  </g>
                );
              }}
            />
      </div>
    </AnalyticChartPanel>
  );
};
